"use client";

import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  clearDevDiagnostics,
  getDevDiagnosticsSnapshot,
  getEmptyDevDiagnosticsSnapshot,
  subscribeDevDiagnostics,
  type DiagnosticStatus,
} from "@/app/lib/devDiagnostics";

type PanelTab = "http" | "tanstack";

function formatDuration(durationMs: number): string {
  if (durationMs < 10) {
    return `${durationMs.toFixed(1)} ms`;
  }

  return `${Math.round(durationMs)} ms`;
}

function statusClassName(status: DiagnosticStatus): string {
  switch (status) {
    case "success":
      return "bg-success/10 text-success";
    case "error":
      return "bg-danger/10 text-danger";
    default:
      return "bg-warning/10 text-warning";
  }
}

export function QueryDiagnosticsPanel() {
  const t = useTranslations("devDiagnostics");
  const pathname = usePathname();
  const positionClass = /\/sites\/setup(?:\/|$)/u.test(pathname)
    ? "bottom-20"
    : "bottom-4";
  const snapshot = useSyncExternalStore(
    subscribeDevDiagnostics,
    getDevDiagnosticsSnapshot,
    getEmptyDevDiagnosticsSnapshot,
  );
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<PanelTab>("http");
  const [now, setNow] = useState(() => Date.now());
  const pendingCount = useMemo(
    () => [
      ...snapshot.apiRequests,
      ...snapshot.tanStackOperations,
    ].filter((entry) => entry.status === "pending").length,
    [snapshot],
  );

  useEffect(() => {
    if (!open || pendingCount === 0) {
      return;
    }

    const interval = window.setInterval(() => setNow(Date.now()), 250);

    return () => window.clearInterval(interval);
  }, [open, pendingCount]);

  const durationFor = (startedAt: number, durationMs: number | null) => (
    formatDuration(durationMs ?? Math.max(0, now - startedAt))
  );

  if (!open) {
    return (
      <button
        aria-label={t("open")}
        className={`fixed ${positionClass} right-4 z-[500] flex h-10 items-center gap-2 rounded-full border border-border-strong bg-bg-elevated px-4 text-xs font-bold text-text shadow-dropdown transition hover:bg-surface-hover`}
        onClick={() => setOpen(true)}
        type="button"
      >
        <span aria-hidden="true">⏱</span>
        <span>DEV</span>
        {pendingCount > 0 ? (
          <span className="rounded-full bg-warning/15 px-1.5 py-0.5 text-warning">
            {pendingCount}
          </span>
        ) : null}
      </button>
    );
  }

  const entriesCount = tab === "http"
    ? snapshot.apiRequests.length
    : snapshot.tanStackOperations.length;

  return (
    <section
      aria-label={t("title")}
      className={`fixed ${positionClass} right-4 z-[500] flex max-h-[min(70vh,640px)] w-[min(720px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-border-strong bg-bg-elevated text-text shadow-modal`}
    >
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span aria-hidden="true">⏱</span>
            <h2 className="truncate text-sm font-bold">{t("title")}</h2>
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">
              DEV
            </span>
          </div>
          <p className="mt-0.5 text-xs text-text-muted">{t("description")}</p>
        </div>
        <a
          className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-primary hover:bg-surface-hover hover:underline"
          href="/_profiler"
          rel="noreferrer"
          target="_blank"
        >
          {t("profiler")}
        </a>
        <button
          className="rounded-lg px-2 py-1 text-xs font-semibold text-text-secondary hover:bg-surface-hover hover:text-text"
          onClick={clearDevDiagnostics}
          type="button"
        >
          {t("clear")}
        </button>
        <button
          aria-label={t("close")}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-lg text-text-secondary hover:bg-surface-hover hover:text-text"
          onClick={() => setOpen(false)}
          type="button"
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div className="flex border-b border-border px-3 pt-2" role="tablist">
        <TabButton
          active={tab === "http"}
          count={snapshot.apiRequests.length}
          label={t("tabs.http")}
          onClick={() => setTab("http")}
        />
        <TabButton
          active={tab === "tanstack"}
          count={snapshot.tanStackOperations.length}
          label={t("tabs.tanStack")}
          onClick={() => setTab("tanstack")}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {entriesCount === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-text-muted">
            {t("empty")}
          </div>
        ) : null}

        {tab === "http" ? snapshot.apiRequests.map((request) => (
          <article
            className="border-b border-border/70 px-4 py-3 last:border-b-0"
            key={request.id}
          >
            <div className="flex items-start gap-2">
              <span className="mt-0.5 min-w-12 rounded bg-bg-sunken px-1.5 py-1 text-center font-mono text-[10px] font-bold text-text-secondary">
                {request.method}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <code className="truncate text-xs text-text" title={request.path}>
                    {request.path}
                  </code>
                  {request.profilerUrl ? (
                    <a
                      className="shrink-0 text-xs font-semibold text-primary hover:underline"
                      href={request.profilerUrl}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {t("profiler")}
                    </a>
                  ) : null}
                </div>
                {request.serverTiming.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-1 text-[10px] text-text-muted">
                    {request.serverTiming.map((metric) => (
                      <span key={`${request.id}-${metric.name}`}>
                        <span className="font-mono">{metric.name}</span>
                        {" "}
                        <strong className="font-semibold text-text-secondary">
                          {formatDuration(metric.durationMs)}
                        </strong>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              <StatusBadge
                label={t(`status.${request.status}`)}
                status={request.status}
                statusCode={request.statusCode}
              />
              <span className="w-20 shrink-0 text-right font-mono text-xs font-semibold tabular-nums">
                {durationFor(request.startedAt, request.durationMs)}
              </span>
            </div>
          </article>
        )) : null}

        {tab === "tanstack" ? snapshot.tanStackOperations.map((operation) => (
          <article
            className="flex items-start gap-2 border-b border-border/70 px-4 py-3 last:border-b-0"
            key={operation.id}
          >
            <span className="mt-0.5 min-w-16 rounded bg-bg-sunken px-1.5 py-1 text-center text-[10px] font-bold uppercase text-text-secondary">
              {t(`kind.${operation.kind}`)}
            </span>
            <code className="min-w-0 flex-1 truncate text-xs text-text" title={operation.key}>
              {operation.key}
            </code>
            <StatusBadge
              label={t(`status.${operation.status}`)}
              status={operation.status}
              statusCode={null}
            />
            <span className="w-20 shrink-0 text-right font-mono text-xs font-semibold tabular-nums">
              {durationFor(operation.startedAt, operation.durationMs)}
            </span>
          </article>
        )) : null}
      </div>

      <footer className="border-t border-border bg-bg-sunken px-4 py-2 text-[10px] text-text-muted">
        {t("privacyNote")}
      </footer>
    </section>
  );
}

function TabButton({
  active,
  count,
  label,
  onClick,
}: {
  active: boolean;
  count: number;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      aria-selected={active}
      className={[
        "border-b-2 px-3 py-2 text-xs font-semibold transition",
        active
          ? "border-primary text-primary"
          : "border-transparent text-text-muted hover:text-text",
      ].join(" ")}
      onClick={onClick}
      role="tab"
      type="button"
    >
      {label}
      <span className="ml-1.5 rounded-full bg-bg-sunken px-1.5 py-0.5 text-[10px] text-text-secondary">
        {count}
      </span>
    </button>
  );
}

function StatusBadge({
  label,
  status,
  statusCode,
}: {
  label: string;
  status: DiagnosticStatus;
  statusCode: number | null;
}) {
  return (
    <span
      className={[
        "shrink-0 rounded px-1.5 py-1 text-[10px] font-bold",
        statusClassName(status),
      ].join(" ")}
      title={label}
    >
      {statusCode ?? label}
    </span>
  );
}
